import { createStore } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  restaurant: string;
  restaurant_id: number; // Add this field
  kitchen: string;
  kitchen_id: number;
  restaurant_info?: any; // Add this field
  menu_id?: any;
}

interface CartState {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: string) => void;
  decreaseQuantity: (id: string) => void;
  clearCart: () => void;
  clearCartWithoutConfirmation: () => void;
}

let cartStore: any;

// This is our actual store creator function
const createCartStore = () => {
  return createStore<CartState>()(
    persist(
      (set, get) => ({
        items: [],
        
        addItem: (newItem) => {
          set((state) => {
            // Check for different restaurant
            if (state.items.length > 0) {
              const existingRestaurantId = state.items[0].restaurant_id;
              if (existingRestaurantId !== newItem.restaurant_id) {
                return state;
              }
            }
            
            const existingItem = state.items.find(item => item.id === newItem.id);
            
            if (existingItem) {
              return {
                items: state.items.map(item =>
                  item.id === newItem.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
                )
              };
            }
            
            return {
              items: [...state.items, { ...newItem, quantity: 1 }]
            };
          });
        },

        decreaseQuantity: (id) => {
          set((state) => {
            const newItems = state.items.map((item) =>
              item.id === String(id)
                ? { ...item, quantity: Math.max(0, item.quantity - 1) }
                : item
            );
            const filteredItems = newItems.filter((item) => item.quantity > 0);

            return { items: filteredItems };
          });
        },
        
        removeItem: (id) => set((state) => ({
          items: state.items.filter(item => item.id != id)
        })),
        
        clearCart: () => set({ items: [] }),
        
        clearCartWithoutConfirmation: () => set({ items: [] })
      }),
      {
        name: 'cart-storage',
      }
    )
  );
};

// This is a safe client-side hook for Next.js
export const useCartStore = () => {
  // For SSR, return a simple object with empty items array and dummy methods
  if (typeof window === 'undefined') {
    return { 
      items: [],
      addItem: () => {},
      removeItem: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      decreaseQuantity: () => {},
      clearCartWithoutConfirmation: () => {}
    };
  }
  
  // For CSR, use the same store for each user
  if (!cartStore) {
    cartStore = createCartStore();
  }
  
  return cartStore;
};
